/**
 * Pure helpers that compute a pre-commit preview of the name changes a
 * name-scoping transformation (AddPrefix / RemovePrefix / RenameResource) will
 * make, so the user can confirm the effect before approving the transform job.
 *
 * The logic mirrors the backend strategies:
 *  - AddPrefix    : prepend `prefix` unless the name already starts with it.
 *  - RemovePrefix : strip `prefix` when the name starts with it, else no change.
 *  - RenameResource: exact-match lookup in the `mappings` object.
 *
 * These functions operate on the resource's display name (what the UI shows).
 */

/** Transformation types that rename a resource and therefore support a preview. */
export const NAME_TRANSFORM_TYPES = ['AddPrefix', 'RemovePrefix', 'RenameResource'] as const;

export type NameTransformType = (typeof NAME_TRANSFORM_TYPES)[number];

/** A single before/after preview entry for one resource. */
export interface NamePreviewRow {
    before: string;
    after: string;
    changed: boolean;
}

/** Preview for one resource type (display name), with a small sample of rows. */
export interface NamePreviewGroup {
    /** Display name of the resource type, e.g. "Flows". */
    resourceType: string;
    /** Sample rows to show (changed rows first), capped at the sample limit. */
    rows: NamePreviewRow[];
    /** Total resources of this type available for preview. */
    totalCount: number;
    /** How many of the type's resources would actually change. */
    changedCount: number;
}

/**
 * Returns true when the given transformation type renames resources and thus
 * supports a name-change preview.
 */
export function isNameTransform(transformationType: string): transformationType is NameTransformType {
    return (NAME_TRANSFORM_TYPES as readonly string[]).includes(transformationType);
}

/**
 * Computes the resulting name for a single resource under the given
 * transformation. Returns the original name when nothing changes.
 */
export function applyNameTransform(
    transformationType: string,
    parameters: Record<string, unknown> | undefined,
    name: string
): string {
    switch (transformationType) {
        case 'AddPrefix': {
            const prefix = typeof parameters?.['prefix'] === 'string' ? (parameters['prefix'] as string) : '';
            if (!prefix) return name;
            return name.startsWith(prefix) ? name : prefix + name;
        }
        case 'RemovePrefix': {
            const prefix = typeof parameters?.['prefix'] === 'string' ? (parameters['prefix'] as string) : '';
            if (!prefix) return name;
            return name.startsWith(prefix) ? name.slice(prefix.length) : name;
        }
        case 'RenameResource': {
            const mappings = parameters?.['mappings'];
            if (mappings && typeof mappings === 'object' && !Array.isArray(mappings)) {
                const mapped = (mappings as Record<string, unknown>)[name];
                if (typeof mapped === 'string' && mapped) return mapped;
            }
            return name;
        }
        default:
            return name;
    }
}

/**
 * Builds a grouped preview of the name changes a transformation will make for
 * the selected resource types. Changed rows are surfaced first so the sample is
 * informative even when only a few names match. Groups with no available
 * resources are omitted.
 *
 * @param transformationType One of the NAME_TRANSFORM_TYPES values.
 * @param parameters The transformation's parameters (prefix / mappings).
 * @param resourceTypes The selected resource-type display names to preview.
 * @param resourcesByType Map of resource-type display name -> resource names.
 * @param sampleLimit Max rows to include per type (default 4).
 */
export function computeNamePreview(
    transformationType: string,
    parameters: Record<string, unknown> | undefined,
    resourceTypes: string[],
    resourcesByType: Record<string, string[]>,
    sampleLimit = 4
): NamePreviewGroup[] {
    if (!isNameTransform(transformationType)) return [];

    const groups: NamePreviewGroup[] = [];

    for (const resourceType of resourceTypes) {
        const names = resourcesByType[resourceType] ?? [];
        if (names.length === 0) continue;

        const allRows: NamePreviewRow[] = names.map((before) => {
            const after = applyNameTransform(transformationType, parameters, before);
            return { before, after, changed: after !== before };
        });

        const changedCount = allRows.filter((r) => r.changed).length;

        // Show changed rows first, then unchanged, capped at the sample limit.
        const changedRows = allRows.filter((r) => r.changed);
        const unchangedRows = allRows.filter((r) => !r.changed);
        const rows = [...changedRows, ...unchangedRows].slice(0, sampleLimit);

        groups.push({
            resourceType,
            rows,
            totalCount: names.length,
            changedCount
        });
    }

    return groups;
}
